import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { gbp, num, makeFilters, toParams, fmtDate } from "@/lib/format";
import { FilterBar } from "@/components/FilterBar";
import { PageHeader, KpiCard, Panel } from "@/components/PageHeader";
import { OrderDetailSheet } from "@/components/OrderDetailSheet";
import { DashboardCharts } from "@/components/dashboard/DashboardCharts";
import { DrillBar } from "@/components/dashboard/DrillBar";
import { DrillOrdersSheet } from "@/components/dashboard/DrillOrdersSheet";

const DEFAULT = "12m";

const Pending = ({ items, onOpen }) => (
  <Panel title="Awaiting approval" action={<Link to="/approvals" className="text-xs font-semibold text-[#FF0000] hover:underline" data-testid="dashboard-view-approvals">View queue →</Link>}>
    {items.length === 0 ? <div className="py-8 text-center text-sm text-slate-500">Nothing awaiting approval</div> : (
      <div className="divide-y" data-testid="dashboard-pending-list">{items.map((o) => (
        <button key={o.id} onClick={() => onOpen(o.id)} className="flex w-full items-center justify-between py-3 text-left transition-colors hover:bg-slate-50" data-testid={`dashboard-pending-${o.id}`}>
          <div><div className="font-mono text-xs font-semibold">{o.order_number}</div><div className="text-xs text-slate-500">{o.concession_name || o.store_name} · {fmtDate(o.date_ordered)} · {o.line_count} lines</div></div>
          <div className="font-semibold tabular-nums">{gbp(o.total)}</div>
        </button>))}</div>)}
  </Panel>
);

const Kpis = ({ k }) => (
  <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
    <KpiCard testId="kpi-total-spend" label="Total spend" value={gbp(k.total_spend)} sub={`${num(k.units)} units ordered`} />
    <KpiCard testId="kpi-order-count" label="Orders" value={num(k.order_count)} sub={`${k.rejected_count} rejected`} accent="#00573F" delay={60} />
    <KpiCard testId="kpi-avg-order" label="Avg order value" value={gbp(k.avg_order_value)} sub={`${k.active_locations} ordering locations`} accent="#C5A059" delay={120} />
    <KpiCard testId="kpi-pending" label="Pending approval" value={num(k.pending_count)} sub={gbp(k.pending_value)} accent="#0F172A" delay={180} />
  </div>
);

export default function Dashboard() {
  const [filters, setFilters] = useState(makeFilters(DEFAULT));
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [drillOpen, setDrillOpen] = useState(false);
  const load = () => api.get("/dashboard", { params: toParams(filters) }).then((r) => setD(r.data));
  useEffect(() => { load(); }, [filters]); // eslint-disable-line
  const drill = (patch) => setFilters((f) => (patch ? { ...f, ...patch } : makeFilters(DEFAULT)));
  const count = d ? d.kpis.matching_orders : 0;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Overview" title="Harrods supplies dashboard" />
      <FilterBar filters={filters} setFilters={setFilters} defaultPreset={DEFAULT} show={{ category: true, status: true }} />
      <DrillBar filters={filters} drill={drill} defaultPreset={DEFAULT} count={num(count)} onView={() => setDrillOpen(true)} />
      {d && (<>
        <Kpis k={d.kpis} />
        <DashboardCharts d={d} filters={filters} drill={drill} />
        <Pending items={d.recent_pending} onOpen={setOpen} />
      </>)}
      <DrillOrdersSheet open={drillOpen} onClose={() => setDrillOpen(false)} filters={filters} onOpenOrder={(id) => { setDrillOpen(false); setOpen(id); }} />
      <OrderDetailSheet orderId={open} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
